import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { useIntersection } from '@mantine/hooks';
import _ from 'lodash';
import { formatCalendar } from '../../../../utils/date-value';
import { useProfile } from '../../../../api/profile';
import { trpcInfinite } from '../../../../api/trpc';
import { CustomsDeliveryNoteMessageCard } from './customs-delivery-note-message-card';

type Props = {
  customsDeliveryOrderId: string;
  onNotesMutate?: () => void;
};
export type CustomsDeliveryNotesConversationTabRef = {
  mutateNotes: () => void;
};

export const CustomsDeliveryNotesConversationTab = forwardRef<
  CustomsDeliveryNotesConversationTabRef,
  Props
>((props, ref) => {
  const PAGE_SIZE = 10;

  const { data, isLoading, size, setSize, mutate } =
    trpcInfinite.customsDeliveryNotes.getMany.use((_, previousPageData) => {
      if (previousPageData && !previousPageData.length) return null;
      return {
        customsDeliveryOrderId: props.customsDeliveryOrderId,
        noteId: previousPageData
          ? previousPageData[previousPageData.length - 1].id
          : undefined,
      };
    });

  const { data: profile } = useProfile();

  const isEmpty = data?.[0]?.length === 0;
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);
  const timeMessages = React.useMemo(() => {
    return _.chain(data)
      .flatten()
      .groupBy((message) => {
        return formatCalendar(message.createdAt);
      })
      .map((value, key) => ({ day: key, messages: value }))
      .value();
  }, [data]);
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: interRef, entry } = useIntersection({
    root: containerRef.current,
    threshold: 1,
  });
  const lastMessage = React.useMemo(() => {
    if (!data?.length) {
      return undefined;
    }
    const messages = data[data.length - 1];
    return messages.length ? messages[messages.length - 1].id : undefined;
  }, [data]);

  useImperativeHandle(
    ref,
    () => ({
      mutateNotes: () => mutate(),
    }),
    [mutate],
  );

  React.useEffect(() => {
    if (entry?.isIntersecting && !isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  }, [entry, isLoadingMore, isReachingEnd, setSize, size]);

  return (
    <>
      {timeMessages.map(({ day, messages }) => (
        <>
          {messages.map((message) => (
            <CustomsDeliveryNoteMessageCard
              innerRef={lastMessage === message.id ? interRef : undefined}
              self={profile?.id === message.user.id}
              message={message}
              onDelete={() => mutate()}
              key={message.id}
              orderId={props.customsDeliveryOrderId}
            />
          ))}
          <div className="align-self-center">{day}</div>
        </>
      ))}
    </>
  );
});
